import { useEffect, useState } from 'react';
import { AuthShell } from './AuthShell';
import { useAuthStore } from '@/stores/auth';

// /auth/logout — Cognito's redirect target after a successful sign-out.
// At this point Cognito has dropped its SSO cookie; this page also
// clears PMA's local auth store (token + user profile) on mount, then
// confirms the sign-out and offers a way back in.
//
// Clearing the store HERE rather than in TopBar's handleLogout matters:
// PMA persists the auth store to localStorage. If handleLogout cleared
// the store before redirectToLogout, React would re-render RequireAuth
// with token=null, fire its own redirectToHostedUi, and cancel the
// /logout navigation via window.location.assign collision — Cognito's
// SSO cookie would never drop and the user would auto-sign-in on the
// next mount. We let the Cognito round-trip complete first, then wipe
// the local store here so the "Sign in again" link genuinely re-prompts.
//
// Idle-timeout sign-outs set sessionStorage['gs.signout.reason']='idle'
// before redirecting through Cognito; we read and clear it here so the
// user knows *why* they were signed out (vs. a deliberate click).

export function AuthLogoutPage() {
    const [reason, setReason] = useState<string | null>(null);
    useEffect(() => {
        // Clear the persisted auth store now that Cognito's logout has
        // completed. Without this, the persisted token survives in
        // localStorage and RequireAuth waves the user straight through
        // on the next navigation back into the app.
        useAuthStore.getState().logout();
        try {
            const r = sessionStorage.getItem('gs.signout.reason');
            if (r) {
                setReason(r);
                sessionStorage.removeItem('gs.signout.reason');
            }
        } catch { /* private mode / disabled storage — fall through */ }
    }, []);

    const heading = reason === 'idle' ? 'Signed out — inactivity' : 'Signed out';
    const body = reason === 'idle'
        ? 'You were signed out after 15 minutes of inactivity. Sign in again to continue.'
        : 'You have been signed out.';

    return (
        <AuthShell>
            <div className="space-y-3 text-center">
                <h1 className="text-xl font-semibold text-text">{heading}</h1>
                <p className="text-sm text-text-subtle">{body}</p>
                {/*
                  * "/" lands inside AppShell → RequireAuth. With no
                  * in-memory token, RequireAuth's auth-gate effect
                  * re-runs the IdP redirect — single-click re-sign-in.
                  */}
                <a href="/" className="text-accent-text underline text-sm">
                    Sign in again
                </a>
            </div>
        </AuthShell>
    );
}
